import { FC } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '../Logo';
import { PACKAGES, packageDocsHref } from '@/constants';

export const Footer: FC = () => {
  return (
    <footer className="fs-glass-strong border-t border-theme-border py-16">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-4 gap-12">

          <div className="md:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-4">
              <Logo size={40} />
              <span className="text-xl font-bold text-theme-primary">ForgeStack</span>
            </Link>
            <p className="text-theme-muted text-sm">
              An open-source ecosystem of React and Node.js libraries under @forgedevstack.
            </p>
          </div>


          <div>
            <h4 className="font-semibold text-theme-primary mb-4">Packages</h4>
            <ul className="space-y-2">
              {PACKAGES.map((pkg) => (
                <li key={pkg.id}>
                  <Link
                    to={packageDocsHref(pkg)}
                    className={`text-sm transition-colors ${
                      pkg.status === 'ready'
                        ? 'text-theme-muted hover:text-theme-primary'
                        : 'text-theme-muted/50 cursor-not-allowed'
                    }`}
                  >
                    {pkg.name}
                    {pkg.status !== 'ready' && (
                      <span className="ml-2 text-xs opacity-60">
                        {pkg.status === 'coming-soon' ? '(Soon)' : '(Planned)'}
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </div>


          <div>
            <h4 className="font-semibold text-theme-primary mb-4">Resources</h4>
            <ul className="space-y-2">
              <li>
                <a
                  href="https://github.com/yaghobieh/ForgeStack"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-theme-muted hover:text-theme-primary transition-colors"
                >
                  GitHub
                </a>
              </li>
              <li>
                <a
                  href="https://www.npmjs.com/org/forgedevstack"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-theme-muted hover:text-theme-primary transition-colors"
                >
                  npm
                </a>
              </li>
              <li>
                <Link
                  to="/harbor/docs/quick-start"
                  className="text-sm text-theme-muted hover:text-theme-primary transition-colors"
                >
                  Documentation
                </Link>
              </li>
            </ul>
          </div>


          <div>
            <h4 className="font-semibold text-theme-primary mb-4">Community</h4>
            <ul className="space-y-2">
              <li>
                <a
                  href="https://github.com/yaghobieh/ForgeStack/issues"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-theme-muted hover:text-theme-primary transition-colors"
                >
                  Issues
                </a>
              </li>
              <li>
                <a
                  href="https://github.com/yaghobieh/ForgeStack/discussions"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-theme-muted hover:text-theme-primary transition-colors"
                >
                  Discussions
                </a>
              </li>
            </ul>
          </div>
        </div>

          <div className="border-t border-theme-border mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4 text-sm text-theme-muted">
            <span>© {new Date().getFullYear()} ForgeStack. MIT License.</span>
            <Link to="/privacy" className="hover:text-theme-primary transition-colors">
              Privacy Policy
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="https://www.linkedin.com/in/john-yaghobieh-4baa93107/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-theme-muted hover:text-theme-primary transition-colors font-medium"
            >
              John Yaghobieh
            </a>
            <a
              href="https://github.com/yaghobieh/ForgeStack"
              target="_blank"
              rel="noopener noreferrer"
              className="text-theme-muted hover:text-theme-primary transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
              </svg>
            </a>
            <a
              href="https://www.linkedin.com/in/john-yaghobieh-4baa93107/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-theme-muted hover:text-theme-primary transition-colors"
              title="LinkedIn"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
